import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccountStore } from '@components/Stores/accountStore';
import { useExposureStore, GENERAL_ID } from '@components/Stores/exposureDataStore';
import { FinancialPositionsGeneral } from './FinancialPositionsGeneral';
import { useFinancialPositions } from '@hooks/useFinancialPositions';
import { useFinancialPositionsStore } from '@components/Stores/FinancialPositionStore';
import { ExposureHorizonTableBanner } from '../../components/ExposureHorizonTableBanner';
import { getExposureWindow } from '../../utils/exposureHorizon';
import { getHedgeTerm } from '../../utils/categoryTerms';
import { splitByHorizon, filterByFormula, getUniqueFormulaCodes } from '../../utils/positionUtils';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';

export const FinancialPositions: React.FC = () => {
  const navigate = useNavigate();
  const { formulaId } = useParams();

  const { account } = useAccountStore();
  const { currentSelectedFormulaId, setCurrentSelectedFormulaId } = useExposureStore();

  useFinancialPositions();
  const { hedgePositions } = useFinancialPositionsStore();
  const [showOutOfScope, setShowOutOfScope] = React.useState(false);

  // Calculate viewing horizon and filter positions
  const horizonMonths = account?.viewingHorizonMonths || 12;
  const exposureWindow = React.useMemo(() => getExposureWindow(horizonMonths), [horizonMonths]);

  const { inScope: inScopePositions, outOfScope: outOfScopePositions } = React.useMemo(
    () => splitByHorizon(hedgePositions, exposureWindow),
    [hedgePositions, exposureWindow],
  );

  // showOutOfScope=false: show only in-scope; true: show all
  const displayPositions = React.useMemo(
    () => (showOutOfScope ? hedgePositions : inScopePositions),
    [showOutOfScope, hedgePositions, inScopePositions],
  );

  // In formula-specific mode, filter to that formula
  const positionsForView = React.useMemo(
    () => (formulaId && formulaId !== GENERAL_ID ? filterByFormula(displayPositions, formulaId) : displayPositions),
    [displayPositions, formulaId],
  );

  React.useEffect(() => {
    setCurrentSelectedFormulaId(formulaId || GENERAL_ID);
  }, [formulaId]);

  const setMaterial = (formulaId: string) => {
    setCurrentSelectedFormulaId(formulaId);
    navigate('/hedges/' + (formulaId && formulaId !== GENERAL_ID ? formulaId : ''));
  };

  const isGeneralMode = !formulaId || formulaId === GENERAL_ID;

  // Only show formula cards for formulas that have hedge positions
  const formulaCodesWithHedges = React.useMemo(() => getUniqueFormulaCodes(hedgePositions), [hedgePositions]);

  // Configure shared formula card bar
  useCardBar({ filterFormulaCodes: formulaCodesWithHedges, onSelectPath: '/hedges' });

  return (
    <div className="flex flex-col h-full space-y-[1rem]">
      <ExposureHorizonTableBanner
        window={exposureWindow}
        inScopeCount={inScopePositions.length}
        outOfScopeCount={outOfScopePositions.length}
        totalCount={hedgePositions?.length || 0}
        showOutOfScope={showOutOfScope}
        onToggleOutOfScope={setShowOutOfScope}
        categoryName={getHedgeTerm(account)}
      />
      <FinancialPositionsGeneral
        positions={positionsForView}
        setMaterial={setMaterial}
        isGeneralMode={isGeneralMode}
        formulaId={!isGeneralMode ? formulaId : undefined}
        exposureWindow={exposureWindow}
      />
    </div>
  );
};
